import { z } from 'zod';
import { SHOP_TIME_ZONE, SHOP_UTC_OFFSET, shopDateInputValue } from '../money/accounting-period.js';

/**
 * The date range every report is bounded by, and the presets an owner actually picks.
 *
 * Two rules, and they are the same rule seen from both ends.
 *
 * **A report is never unbounded.** `from` and `to` are required by the schema rather than
 * defaulted to "everything", because an unbounded aggregation over three years of orders is
 * the query that takes a shop's dashboard down in its busiest month. The bound is part of the
 * contract, so a caller cannot omit it and a handler cannot forget to apply it.
 *
 * **The boundary is the shop's, not the server's.** A payment taken at 11pm in Coimbatore
 * belongs to that day, and a server keeping UTC would file it under tomorrow. Every range
 * resolved here is `[start, end)` at IST midnight, exactly as `monthBounds` does for a month —
 * so a row can never fall into two ranges or into none.
 *
 * Presets are resolved HERE rather than on each side, so the picker on the phone and the
 * aggregation on the server cannot disagree about where "last 30 days" begins.
 */

/** The ranges the picker offers, plus the escape hatch for a hand-typed one. */
export const REPORT_PRESETS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'this_month',
  'last_month',
  'this_financial_year',
  'custom',
] as const;
export type ReportPreset = (typeof REPORT_PRESETS)[number];
export const reportPresetSchema = z.enum(REPORT_PRESETS);

/** A `YYYY-MM-DD` day in the shop's timezone — what `<input type="date">` produces. */
export const DAY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const shopDaySchema = z.string().regex(DAY_PATTERN, 'Enter a date as YYYY-MM-DD');

export interface DateRange {
  /** Inclusive. IST midnight at the start of `fromDay`. */
  start: Date;
  /** EXCLUSIVE. IST midnight at the start of the day after `toDay`. */
  end: Date;
  fromDay: string;
  toDay: string;
}

/** IST midnight at the start of the given shop day. */
export function shopDayStart(day: string): Date {
  if (!DAY_PATTERN.test(day)) {
    throw new RangeError(`Not a valid day: "${day}". Expected YYYY-MM-DD.`);
  }
  return new Date(`${day}T00:00:00${SHOP_UTC_OFFSET}`);
}

/** The shop day `days` after `day`, as `YYYY-MM-DD`. Negative counts go backwards. */
export function addShopDays(day: string, days: number): string {
  const shifted = new Date(shopDayStart(day).getTime() + days * 86_400_000);
  return shopDateInputValue(shifted, SHOP_TIME_ZONE);
}

/** `[start, end)` covering the whole of one shop day — what "due today" is measured against. */
export function shopDayBounds(at: Date, timeZone: string = SHOP_TIME_ZONE): DateRange {
  const day = shopDateInputValue(at, timeZone);
  return rangeOfDays(day, day);
}

/** `[start, end)` spanning two shop days inclusive. */
export function rangeOfDays(fromDay: string, toDay: string): DateRange {
  return {
    start: shopDayStart(fromDay),
    end: shopDayStart(addShopDays(toDay, 1)),
    fromDay,
    toDay,
  };
}

/** The Indian financial year (April–March) containing a shop day, as `2026-27`. */
function financialYearStartDay(day: string): string {
  const [year, month] = day.split('-').map(Number) as [number, number];
  const startYear = month >= 4 ? year : year - 1;
  return `${String(startYear).padStart(4, '0')}-04-01`;
}

/**
 * A preset, resolved against the moment the owner opened the screen.
 *
 * `custom` needs the days it was given; every other preset computes them, and the returned
 * range carries the resolved days so the screen can show what it actually asked for rather
 * than the word it was asked by.
 */
export function resolvePreset(
  preset: ReportPreset,
  now: Date,
  custom?: { fromDay?: string | undefined; toDay?: string | undefined },
): DateRange {
  const today = shopDateInputValue(now, SHOP_TIME_ZONE);

  switch (preset) {
    case 'today':
      return rangeOfDays(today, today);
    case 'yesterday': {
      const day = addShopDays(today, -1);
      return rangeOfDays(day, day);
    }
    // Inclusive of today, so "last 7 days" is a week ending now rather than a week ending
    // yesterday — which is what somebody who taps it at 4pm means by it.
    case 'last_7_days':
      return rangeOfDays(addShopDays(today, -6), today);
    case 'last_30_days':
      return rangeOfDays(addShopDays(today, -29), today);
    case 'last_90_days':
      return rangeOfDays(addShopDays(today, -89), today);
    case 'this_month':
      return rangeOfDays(`${today.slice(0, 7)}-01`, today);
    case 'last_month': {
      const firstOfThis = `${today.slice(0, 7)}-01`;
      const lastOfPrevious = addShopDays(firstOfThis, -1);
      return rangeOfDays(`${lastOfPrevious.slice(0, 7)}-01`, lastOfPrevious);
    }
    case 'this_financial_year':
      return rangeOfDays(financialYearStartDay(today), today);
    case 'custom': {
      const fromDay = custom?.fromDay ?? today;
      const toDay = custom?.toDay ?? today;
      return rangeOfDays(fromDay, toDay);
    }
  }
}

/**
 * The longest span a report may be asked for.
 *
 * Three years plus a day of slack. Long enough for "since we opened" on a shop this product
 * has existed for, and short enough that no single request can ask the database to walk a
 * decade — which is the request that would otherwise arrive the first time somebody typed
 * 1970 into a date input by accident.
 */
export const MAX_REPORT_DAYS = 1_097;

export const dateRangeQuerySchema = z
  .object({
    preset: reportPresetSchema.default('last_30_days'),
    from: shopDaySchema.optional(),
    to: shopDaySchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.preset !== 'custom') return;
    if (!value.from || !value.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a start and an end date',
        path: [value.from ? 'to' : 'from'],
      });
      return;
    }
    if (value.from > value.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The start date must not be after the end date',
        path: ['from'],
      });
      return;
    }
    const spanDays =
      Math.round(
        (shopDayStart(value.to).getTime() - shopDayStart(value.from).getTime()) / 86_400_000,
      ) + 1;
    if (spanDays > MAX_REPORT_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a shorter period — reports cover at most three years at a time',
        path: ['to'],
      });
    }
  });

export type DateRangeQuery = z.infer<typeof dateRangeQuerySchema>;

/** The query as a range. The one function both the server and the client resolve through. */
export function queryRange(query: DateRangeQuery, now: Date = new Date()): DateRange {
  return resolvePreset(query.preset, now, { fromDay: query.from, toDay: query.to });
}

/** How the range is described on the screen and in the exported file's header. */
export function describeRange(range: DateRange): string {
  return range.fromDay === range.toDay ? range.fromDay : `${range.fromDay} to ${range.toDay}`;
}

/**
 * The range of the same length immediately before this one — what a period-on-period
 * comparison is drawn against.
 *
 * Length is counted in whole shop days, so comparing a 31-day month against the 30-day one
 * before it compares 31 days with 31 days. A comparison that silently changed the length of
 * the window would make every February look catastrophic.
 */
export function precedingRange(range: DateRange): DateRange {
  const days = Math.round((range.end.getTime() - range.start.getTime()) / 86_400_000);
  const toDay = addShopDays(range.fromDay, -1);
  return rangeOfDays(addShopDays(toDay, -(days - 1)), toDay);
}
